import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Upload, ListOrdered, Wallet, Printer, Users,
  Settings as SettingsIcon, BarChart3, LogOut, Menu, X, Sun, Moon, Bell, ChevronDown,
  Copy, Image as ImageIcon, UserSquare, IdCard as IdIcon, Cpu, Languages, QrCode,
  Crown, Banknote, LifeBuoy, Info, ShieldCheck, Store, CreditCard, Wrench, IdCard,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuRadioGroup, DropdownMenuRadioItem,
} from "@/components/ui/dropdown-menu";
import Logo from "@/components/Logo";
import PassportPaperConfirmModal from "@/components/PassportPaperConfirmModal";

const ROLE_COLOR = {
  super_admin: "bg-brand-blue text-white",
  distributor: "bg-purple-600 text-white",
  retailer: "bg-brand-green text-white",
  customer: "bg-amber-500 text-white",
};

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const { t, locale, setLocale, languages } = useI18n();
  const loc = useLocation();
  const nav = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  if (!user) return children;

  // Studio pages are available to customer + retailer (retailer's "Print Studio").
  const STUDIO_ROLES = ["customer", "retailer", "super_admin"];

  const NAV = [
    { to: "/dashboard", label: t("nav.dashboard"), icon: LayoutDashboard,
      roles: ["super_admin", "distributor", "retailer", "customer"] },

    // Retailer's Print Studio (also visible to customers & admin for demos)
    { to: "/print", label: t("nav.newPrint"), icon: Upload, roles: STUDIO_ROLES, group: "studio" },
    { to: "/bulk-print", label: t("nav.bulkPrint"), icon: Copy, roles: STUDIO_ROLES, group: "studio" },
    { to: "/image-print", label: t("nav.imagePrint"), icon: ImageIcon, roles: STUDIO_ROLES, group: "studio" },
    { to: "/passport-photo", label: t("nav.passport"), icon: UserSquare, roles: STUDIO_ROLES, group: "studio" },
    { to: "/id-card", label: t("nav.idCard"), icon: IdIcon, roles: STUDIO_ROLES, group: "studio" },
    { to: "/pvc-cutter", label: "PVC Card Cutter", icon: CreditCard, roles: ["retailer", "super_admin"], group: "studio" },
    { to: "/id-card-a4", label: "ID Card → A4", icon: IdCard, roles: ["retailer", "super_admin"], group: "studio" },
    { to: "/free-tools", label: "Free Tools", icon: Wrench, roles: ["retailer", "super_admin"], group: "studio" },

    { to: "/orders", label: t("nav.orders"), icon: ListOrdered,
      roles: ["super_admin", "distributor", "retailer", "customer"] },
    { to: "/wallet", label: t("nav.wallet"), icon: Wallet,
      roles: ["super_admin", "distributor", "retailer", "customer"] },
    { to: "/printers", label: t("nav.printers"), icon: Printer,
      roles: ["super_admin", "retailer"] },
    { to: "/connector", label: t("nav.connector"), icon: Cpu,
      roles: ["super_admin", "retailer", "distributor"] },
    { to: "/my-shop", label: t("nav.myShop"), icon: QrCode, roles: ["super_admin", "retailer"] },
    { to: "/subscription", label: t("nav.subscription"), icon: Crown,
      roles: ["retailer", "distributor"] },

    { to: "/reports", label: t("nav.reports"), icon: BarChart3,
      roles: ["super_admin", "distributor", "retailer"] },
    { to: "/admin/dashboard", label: "Admin overview", icon: ShieldCheck, roles: ["super_admin"], group: "admin" },
    { to: "/admin/retailers", label: "Retailers", icon: Store, roles: ["super_admin"], group: "admin" },
    { to: "/admin/users", label: t("nav.users"), icon: Users, roles: ["super_admin"], group: "admin" },
    { to: "/admin/finance", label: t("nav.finance"), icon: Banknote, roles: ["super_admin"], group: "admin" },
    { to: "/settings", label: t("nav.settings"), icon: SettingsIcon, roles: ["super_admin"], group: "admin" },

    { to: "/help", label: t("nav.help"), icon: LifeBuoy,
      roles: ["super_admin", "distributor", "retailer", "customer"] },
    { to: "/about", label: t("nav.about"), icon: Info,
      roles: ["super_admin", "distributor", "retailer", "customer"] },
  ];

  const items = NAV.filter((n) => n.roles.includes(user.role));

  const doLogout = async () => {
    await logout();
    nav("/login");
  };

  const initials = (user.name || user.email || "?")
    .split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const roleLabel = t(`auth.role_${user.role}`);
  const currentLang = languages.find((l) => l.code === locale);

  return (
    <div className="min-h-screen bg-background flex">
      <aside
        className={`${mobileOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0
          fixed lg:sticky top-0 left-0 z-40 h-screen w-64 bg-card border-r border-border
          transition-transform duration-200 flex flex-col`}
        data-testid="sidebar"
      >
        <div className="p-5 border-b border-border">
          <Logo size={36} />
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {items.map((item, idx) => {
            const Icon = item.icon;
            const active = loc.pathname === item.to || loc.pathname.startsWith(item.to + "/");
            // Insert a subtle group label at the start of a new group cluster.
            const isFirstStudio = item.group === "studio" && items[idx - 1]?.group !== "studio";
            const isFirstAdmin = item.group === "admin" && items[idx - 1]?.group !== "admin";
            return (
              <React.Fragment key={item.to}>
                {isFirstStudio && (
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground px-3 pt-3 pb-1">
                    {t("nav.printStudio")}
                  </p>
                )}
                {isFirstAdmin && (
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground px-3 pt-3 pb-1">
                    Super Admin
                  </p>
                )}
                <Link
                  to={item.to}
                  onClick={() => setMobileOpen(false)}
                  data-testid={`nav-${item.to.replace(/\//g, "-").replace(/^-/, "")}`}
                  className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors
                    ${active
                      ? "bg-primary/10 text-primary"
                      : "text-muted-foreground hover:text-foreground hover:bg-secondary"}`}
                >
                  <Icon size={17} />
                  <span>{item.label}</span>
                </Link>
              </React.Fragment>
            );
          })}
        </nav>
        <div className="p-4 border-t border-border">
          <div className="flex items-center gap-3 mb-3">
            <Avatar className="h-9 w-9">
              <AvatarFallback className="bg-primary text-primary-foreground text-xs">{initials}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate" data-testid="sidebar-user-name">{user.name}</p>
              <Badge className={`${ROLE_COLOR[user.role]} text-[10px] mt-0.5 px-1.5 py-0`}>{roleLabel}</Badge>
            </div>
          </div>
          <Button variant="ghost" size="sm" className="w-full justify-start text-muted-foreground hover:text-destructive"
            onClick={doLogout} data-testid="sidebar-logout-btn">
            <LogOut size={16} className="mr-2" /> {t("common.signOut")}
          </Button>
        </div>
      </aside>

      {mobileOpen && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setMobileOpen(false)} />}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-20 bg-background/85 backdrop-blur border-b border-border">
          <div className="flex items-center justify-between px-4 lg:px-8 h-16">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle">
                {mobileOpen ? <X size={20} /> : <Menu size={20} />}
              </Button>
              <div className="hidden md:block">
                <p className="text-xs uppercase tracking-widest text-muted-foreground">MaaPrint · {t("brand.tagline")}</p>
                <p className="text-sm font-semibold">
                  {t("common.welcome")}, <span className="text-primary">{user.name.split(" ")[0]}</span>
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1 sm:gap-2">
              {/* Language selector */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" data-testid="language-selector-btn" className="gap-1.5">
                    <Languages size={16} />
                    <span className="text-xs font-medium hidden sm:inline">{currentLang?.native || "EN"}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-40">
                  <DropdownMenuLabel>{t("common.language")}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuRadioGroup value={locale} onValueChange={setLocale}>
                    {languages.map((l) => (
                      <DropdownMenuRadioItem key={l.code} value={l.code} data-testid={`lang-${l.code}`}>
                        {l.native} <span className="ml-auto text-xs text-muted-foreground">{l.label}</span>
                      </DropdownMenuRadioItem>
                    ))}
                  </DropdownMenuRadioGroup>
                </DropdownMenuContent>
              </DropdownMenu>

              <Button variant="ghost" size="icon" onClick={toggle} data-testid="theme-toggle" aria-label="Toggle theme">
                {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
              </Button>
              <Button variant="ghost" size="icon" data-testid="notifications-btn" aria-label="Notifications">
                <Bell size={18} />
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="gap-2 pl-1 pr-2" data-testid="user-menu-btn">
                    <Avatar className="h-7 w-7">
                      <AvatarFallback className="bg-primary text-primary-foreground text-[10px]">{initials}</AvatarFallback>
                    </Avatar>
                    <ChevronDown size={14} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <p className="text-sm">{user.name}</p>
                    <p className="text-xs text-muted-foreground">{user.email}</p>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => nav("/wallet")} data-testid="menu-wallet">
                    <Wallet size={14} className="mr-2" /> {t("nav.wallet")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={doLogout} data-testid="menu-logout" className="text-destructive">
                    <LogOut size={14} className="mr-2" /> {t("common.signOut")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-8 max-w-[1600px] w-full mx-auto animate-fade-in-up">
          {children}
        </main>
      </div>
      {/* Pops full-screen when a passport photo order lands in the retailer's
          queue — retailer must load 4×6" glossy paper and confirm before print. */}
      <PassportPaperConfirmModal />
    </div>
  );
}
